import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, MapPin, Database, Compass, Lock } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { Spark } from "@/shared/components/brand/Spark";
import { Button } from "@/shared/components/ui/button";
import { InfoHint } from "@/shared/components/ui/info-hint";
import { Card } from "@/shared/components/ui/card";
import { cn } from "@/shared/lib/utils";
import { useCampaignsList, useCreateCampaign } from "./use-campaigns";
import { useConnectors, useRankMarkets } from "./use-connectors";
import type { ConnectorDescriptor, DiscoverySource } from "./types";

const ICONS: Record<string, LucideIcon> = { AI: Sparkles, PLACES: MapPin, AIRDNA: Compass, APOLLO: Database, CSV: Database };

/**
 * New campaign (ADR-0019) — ONE place to point the engine at a market: describe it,
 * pick where the data comes from (a real connector picker), optionally rank markets
 * first, then run. Creating the campaign kicks off discovery on it directly (no
 * second screen that re-asks the same parameters).
 */
export function NewCampaignPage() {
  const navigate = useNavigate();
  const create = useCreateCampaign();
  const recent = useCampaignsList();
  const connectors = useConnectors();
  const rank = useRankMarkets();

  const [prompt, setPrompt] = useState("");
  const [vertical, setVertical] = useState("");
  const [source, setSource] = useState<DiscoverySource>("AI");

  const hasBrief = prompt.trim().length > 0;
  const discoveryConnectors = (connectors.data ?? []).filter((c) => c.capabilities.includes("DISCOVERY"));
  const canRankMarkets = (connectors.data ?? []).some((c) => c.capabilities.includes("MARKET_RANKING") && c.available);

  async function run() {
    if (!hasBrief || create.isPending) return;
    // ADR-0023: the brief (+ source) is PERSISTED on the campaign; the server derives a
    // short name from it. We only carry `autostart` so Discovery kicks off once on arrival
    // — the brief itself is read back from the campaign, so it survives reloads/revisits.
    const campaign = await create.mutateAsync({
      brief: prompt.trim(),
      vertical: vertical.trim() || undefined,
      source,
    });
    navigate(`/campaigns/${campaign.id}/discovery`, { state: { autostart: true } });
  }

  function addMarket(label: string) {
    setPrompt((p) => (p.trim() ? `${p.trim()} in ${label}` : `Businesses in ${label}`));
  }

  return (
    <div className="mx-auto max-w-3xl">
      <p className="ge-eyebrow !text-ge-accent">NEW CAMPAIGN</p>
      <h1 className="mt-2 font-sans text-[26px] font-bold tracking-[-0.3px] text-ge-primary">
        Point the engine at a market
      </h1>
      <p className="mt-2 text-[14.5px] text-ge-secondary">
        Describe who you want to reach and where the data comes from. The engine discovers, screens, and
        lines them up for outreach.
      </p>

      {/* One command bar: brief + target type + source + run */}
      <Card className="mt-6 p-[18px]">
        <div className="flex gap-3">
          <Spark size={16} className="mt-1 shrink-0" />
          <textarea
            data-testid="discovery-prompt"
            rows={2}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="e.g. Independent strength gyms and CrossFit boxes in Austin, TX with strong reviews"
            className="w-full resize-none bg-transparent text-[15px] leading-[1.5] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none"
          />
        </div>

        {/* Source picker (real — from the connector registry) */}
        <div className="mt-4">
          <p className="ge-eyebrow pb-2 flex items-center gap-1">DATA SOURCE <InfoHint text="Where leads come from: AI (the model proposes businesses), Places (Google Maps), or AirDNA. Each is screened for duplicates and already-registered targets." /></p>
          <div className="flex flex-wrap gap-2" data-testid="source-picker">
            {connectors.isLoading && <span className="text-[12px] text-ge-muted">Loading sources…</span>}
            {discoveryConnectors.map((c) => (
              <SourceOption
                key={c.key}
                connector={c}
                selected={source === c.key}
                onSelect={() => setSource(c.key as DiscoverySource)}
              />
            ))}
          </div>
        </div>

        {/* Optional: target type (vertical) */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <label className="ge-eyebrow flex items-center gap-1">TARGET TYPE (OPTIONAL) <InfoHint text="What you're prospecting (e.g. gyms, short-term rentals). Sharpens the discovery and copy prompts. Leave blank if your brief already says it." /></label>
          <input
            data-testid="target-type"
            value={vertical}
            onChange={(e) => setVertical(e.target.value)}
            placeholder="e.g. gyms, short-term rentals, pharmacies"
            className="min-w-[220px] flex-1 rounded-ge-control border border-ge-border-card bg-transparent px-3 py-2 text-[13px] text-ge-primary placeholder:text-ge-muted focus-visible:outline-none"
          />
          <Button data-testid="run-discovery" disabled={!hasBrief || create.isPending} onClick={() => void run()}>
            {create.isPending ? "Creating…" : "Run discovery"}
            <ArrowRight className="h-[14px] w-[14px]" strokeWidth={2.4} />
          </Button>
        </div>

        {create.isError && (
          <p className="mt-2 text-[12px] text-ge-danger">Couldn&apos;t create the campaign. Try again.</p>
        )}
      </Card>

      {/* Optional market ranking (AirDNA) */}
      {canRankMarkets && (
        <Card className="mt-4 p-[18px]" data-testid="market-ranking">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[13.5px] font-semibold text-ge-primary">Not sure which market?</p>
              <p className="text-[12.5px] text-ge-secondary">Rank cities by opportunity for your brief, then add one.</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              data-testid="rank-markets"
              disabled={prompt.trim().length < 3 || rank.isPending}
              onClick={() => rank.mutate({ brief: prompt.trim(), limit: 6 })}
            >
              <Compass className="h-[14px] w-[14px]" strokeWidth={1.8} />
              {rank.isPending ? "Ranking…" : "Rank markets"}
            </Button>
          </div>
          {rank.data && (
            <div className="mt-3 flex flex-wrap gap-2">
              {rank.data.markets.map((m) => {
                const label = [m.city, m.state].filter(Boolean).join(", ");
                return (
                  <button
                    key={label}
                    type="button"
                    data-testid={`market-${m.city}`}
                    onClick={() => addMarket(label)}
                    className="inline-flex items-center gap-2 rounded-ge-pill border border-ge-border-card px-3 py-1.5 text-[12.5px] text-ge-value hover:bg-ge-card"
                  >
                    <span className="font-semibold text-ge-primary">{label}</span>
                    <span className="font-mono text-[10.5px] text-ge-accent">{Math.round(m.score * 100)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      )}

      {/* Recent */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <p className="ge-eyebrow">RECENT CAMPAIGNS</p>
          <Link to="/campaigns" className="text-[12px]">View all</Link>
        </div>
        <Card className="mt-3 divide-y divide-ge-border-row">
          {recent.isLoading && <div className="px-4 py-3 text-[12px] text-ge-muted">Loading…</div>}
          {!recent.isLoading && (recent.data?.length ?? 0) === 0 && (
            <div className="px-4 py-3 text-[12px] text-ge-muted">No campaigns yet.</div>
          )}
          {recent.data?.slice(0, 5).map((c) => (
            <Link key={c.id} to={`/campaigns/${c.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-ge-card">
              <span className="text-[13.5px] font-semibold text-ge-primary">{c.name}</span>
              <span className="font-mono text-[11px] text-ge-secondary">{c.status}</span>
            </Link>
          ))}
        </Card>
      </div>
    </div>
  );
}

function SourceOption({
  connector,
  selected,
  onSelect,
}: {
  connector: ConnectorDescriptor;
  selected: boolean;
  onSelect: () => void;
}) {
  const Icon = ICONS[connector.key] ?? Database;
  const disabled = connector.comingSoon || !connector.available;
  return (
    <button
      type="button"
      data-testid={`source-option-${connector.key}`}
      aria-pressed={selected}
      disabled={disabled}
      title={connector.comingSoon ? `${connector.label} — coming soon` : !connector.available ? `${connector.label} — connect a key to enable` : connector.hint}
      onClick={onSelect}
      className={cn(
        "inline-flex items-center gap-2 rounded-ge-control border px-3 py-2 text-[13px] transition-colors",
        selected ? "border-ge-accent bg-ge-accent-tint text-ge-primary" : "border-ge-border-card text-ge-value hover:bg-white/[0.04]",
        disabled && "cursor-not-allowed opacity-45 hover:bg-transparent",
      )}
    >
      <Icon className="h-[15px] w-[15px]" strokeWidth={1.8} />
      {connector.label}
      {disabled && <Lock className="h-[11px] w-[11px] text-ge-faint" strokeWidth={2} />}
    </button>
  );
}
